import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  fetchEmployeeIdByAuthUserId,
  fetchKontrolScoreFeedbackByEmployeeId,
  fetchKontrolScoreFeedbackQuestions,
  getCurrentUser,
  upsertKontrolScoreFeedback,
} from "../api/queries";
import {
  AssessmentHeader,
  AssessmentSectionIntro,
  AssessmentSidebar,
  answerGridSx,
  assessmentContentSx,
  assessmentLayoutSx,
  assessmentOuterSx,
  assessmentOutlineButtonSx,
  assessmentPageSx,
  assessmentPanelSx,
  assessmentPrimaryButtonSx,
  getAnswerOptionSx,
  questionBodySx,
  questionCardSx,
  questionHeaderSx,
  questionLabelSx,
} from "./AssessmentTestLayout";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import type { AssessmentSectionProgress } from "../types/assessment";
import type {
  KontrolScoreFeedbackAnswers,
  KontrolScoreFeedbackOption,
  KontrolScoreFeedbackQuestion,
  KontrolScoreFeedbackSectionKey,
} from "../types/scores";

export const SECTION_ORDER: KontrolScoreFeedbackSectionKey[] = [
  "start",
  "questionnaire",
  "result",
  "recommendation",
  "overall",
];

function isAnswered(question: KontrolScoreFeedbackQuestion, value: string | string[] | undefined) {
  if (!question.is_required) return true;
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(String(value ?? "").trim());
}

export function labelForOption(option: KontrolScoreFeedbackOption, lang: "en" | "de") {
  return lang === "de" ? option.label_de || option.label_en : option.label_en || option.label_de;
}

export function labelForQuestion(question: KontrolScoreFeedbackQuestion, lang: "en" | "de") {
  return lang === "de"
    ? question.question_de || question.question_en
    : question.question_en || question.question_de;
}

function compareQuestionsForDisplay(
  first: KontrolScoreFeedbackQuestion,
  second: KontrolScoreFeedbackQuestion
) {
  if (first.is_required !== second.is_required) return first.is_required ? -1 : 1;
  if (first.order_index !== second.order_index) return first.order_index - second.order_index;
  return first.question_code.localeCompare(second.question_code);
}

/**
 * Feedback questions store a 1-5 scale as 7 options: a textual anchor, the
 * digits 1-5, then a closing textual anchor (e.g. "No idea", 1..5,
 * "Completely clear"). Detected structurally so no schema change is needed:
 * the two anchors render as captions instead of their own selectable button.
 */
function scaleShape(question: KontrolScoreFeedbackQuestion) {
  const options = question.answer_options;
  if (question.input_type !== "single" || options.length < 3) return null;

  const scale = options.slice(1, -1);
  const isNumericSequence = scale.every((option, index) => option.value === String(index + 1));
  if (!isNumericSequence) return null;

  return { low: options[0], high: options[options.length - 1], scale };
}

const scaleToggleGroupSx = {
  width: "100%",
  gap: 0.75,
  "& .MuiToggleButtonGroup-grouped": {
    margin: 0,
    borderRadius: "10px !important",
  },
} as const;

function getScaleButtonSx(selected: boolean) {
  return {
    flex: 1,
    py: 1.15,
    fontWeight: 900,
    fontSize: 15,
    textTransform: "none",
    border: "1px solid",
    borderColor: selected ? brand.ink : brand.border,
    color: selected ? brand.onInkPrimary : brand.textPrimary,
    bgcolor: selected ? brand.ink : brand.surface,
    "&:hover": {
      borderColor: selected ? brand.ink : brand.textFaint,
      bgcolor: selected ? brand.inkDeep : brand.surfaceMuted,
    },
  } as const;
}

function ScaleAnswer(props: {
  shape: { low: KontrolScoreFeedbackOption; high: KontrolScoreFeedbackOption; scale: KontrolScoreFeedbackOption[] };
  value: string;
  lang: "en" | "de";
  onChange: (value: string) => void;
}) {
  const { shape, value, lang, onChange } = props;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75, px: 0.25 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: brand.textSecondary }}>
          {labelForOption(shape.low, lang)}
        </Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: brand.textSecondary, textAlign: "right" }}>
          {labelForOption(shape.high, lang)}
        </Typography>
      </Stack>

      <ToggleButtonGroup
        exclusive
        value={value || null}
        onChange={(_event, next) => {
          if (next) onChange(next);
        }}
        sx={scaleToggleGroupSx}
      >
        {shape.scale.map((option) => {
          const selected = value === option.value;

          return (
            <ToggleButton key={option.value} value={option.value} sx={getScaleButtonSx(selected)}>
              {option.value}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
}

export default function ScoreFeedback(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const sections = React.useMemo(
    () => ({
      start: {
        label: L("First steps", "Erste Schritte"),
        title: L("How clear was the start?", "Wie klar war der Einstieg?"),
        subtitle: L(
          "Tell us whether the KONTROL Score made sense before and during the first questions.",
          "Sag uns, ob der KONTROL Score vor und während der ersten Fragen verständlich war."
        ),
      },
      questionnaire: {
        label: L("Questionnaire", "Fragebogen"),
        title: L("How did the questions feel?", "Wie haben sich die Fragen angefühlt?"),
        subtitle: L(
          "This helps us make the questions clearer, shorter and easier to answer honestly.",
          "Das hilft uns, die Fragen klarer, kürzer und leichter ehrlich beantwortbar zu machen."
        ),
      },
      result: {
        label: L("Result", "Ergebnis"),
        title: L("Did the result feel trustworthy?", "War das Ergebnis nachvollziehbar?"),
        subtitle: L(
          "Your feedback helps us explain why a score appears and what it means.",
          "Dein Feedback hilft uns zu erklären, warum ein Score erscheint und was er bedeutet."
        ),
      },
      recommendation: {
        label: L("Recommendation", "Empfehlung"),
        title: L("Was the action plan relevant?", "War der Aktionsplan relevant?"),
        subtitle: L(
          "Tell us whether the recommended program matched your situation and motivation.",
          "Sag uns, ob das empfohlene Programm zu deiner Situation und Motivation gepasst hat."
        ),
      },
      overall: {
        label: L("Overall", "Gesamteindruck"),
        title: L("What should we improve first?", "Was sollten wir zuerst verbessern?"),
        subtitle: L(
          "One last check so we can focus on the changes that matter most.",
          "Ein letzter Check, damit wir uns auf die wichtigsten Verbesserungen konzentrieren."
        ),
      },
    }),
    [L]
  );

  const [employeeId, setEmployeeId] = React.useState<string | null>(null);
  const [questions, setQuestions] = React.useState<KontrolScoreFeedbackQuestion[]>([]);
  const [answers, setAnswers] = React.useState<KontrolScoreFeedbackAnswers>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr) throw userErr;
        if (!userData?.user) throw new Error(L("Not authenticated.", "Nicht authentifiziert."));

        const { data: employee, error: employeeErr } =
          await fetchEmployeeIdByAuthUserId(userData.user.id);

        if (employeeErr) throw employeeErr;
        if (!employee?.id) throw new Error(L("Employee record not found.", "Mitarbeiterprofil nicht gefunden."));

        const [
          { data: feedback, error: feedbackErr },
          { data: questionRows, error: questionsErr },
        ] = await Promise.all([
          fetchKontrolScoreFeedbackByEmployeeId(employee.id),
          fetchKontrolScoreFeedbackQuestions(),
        ]);

        if (feedbackErr) throw feedbackErr;
        if (questionsErr) throw questionsErr;
        if (!mounted) return;

        setEmployeeId(employee.id);
        setQuestions(questionRows ?? []);
        setAnswers((feedback?.answers ?? {}) as KontrolScoreFeedbackAnswers);
        setSaved(Boolean(feedback?.submitted_at));
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? L("Could not load feedback.", "Feedback konnte nicht geladen werden."));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [L]);

  const requiredQuestions = questions.filter((question) => question.is_required);

  const completedRequired = requiredQuestions.filter((question) =>
    isAnswered(question, answers[question.question_code])
  ).length;
  const canSubmit = completedRequired === requiredQuestions.length && !saving && !loading && questions.length > 0;

  const orderedQuestions = React.useMemo(
    () => [...questions].sort(compareQuestionsForDisplay),
    [questions]
  );

  const displayGroups = React.useMemo(() => {
    const groups: Array<{
      key: string;
      sectionKey: KontrolScoreFeedbackSectionKey;
      startIndex: number;
      questions: KontrolScoreFeedbackQuestion[];
    }> = [];

    orderedQuestions.forEach((question, index) => {
      const current = groups[groups.length - 1];
      if (current?.sectionKey === question.section_key) {
        current.questions.push(question);
        return;
      }

      groups.push({
        key: `${question.section_key}-${index}`,
        sectionKey: question.section_key,
        startIndex: index,
        questions: [question],
      });
    });

    return groups;
  }, [orderedQuestions]);

  const progressSections = SECTION_ORDER.map((key): AssessmentSectionProgress => {
    const sectionQuestions = questions.filter((question) => question.section_key === key && question.is_required);
    const answered = sectionQuestions.filter((question) =>
      isAnswered(question, answers[question.question_code])
    ).length;

    return {
      key,
      label: sections[key].label,
      answered,
      skipped: 0,
      total: sectionQuestions.length,
      active: answered < sectionQuestions.length,
    };
  });

  const activeSectionKey =
    progressSections.find((section) => section.answered < section.total)?.key ?? "overall";

  const progressWithActive = progressSections.map((section) => ({
    ...section,
    active: section.key === activeSectionKey,
  }));

  function setSingleAnswer(key: string, value: string) {
    setSaved(false);
    setAnswers((current) => ({ ...current, [key]: value }));
  }

  function toggleMultiAnswer(key: string, value: string) {
    setSaved(false);
    setAnswers((current) => {
      const values = Array.isArray(current[key]) ? (current[key] as string[]) : [];
      const next = values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value];

      return { ...current, [key]: next };
    });
  }

  async function submit() {
    if (!employeeId || !canSubmit) return;

    setSaving(true);
    setError(null);

    try {
      const { data, error: saveErr } = await upsertKontrolScoreFeedback(employeeId, answers);
      if (saveErr) throw saveErr;
      setAnswers((data?.answers ?? answers) as KontrolScoreFeedbackAnswers);
      setSaved(true);
    } catch (e: any) {
      setError(e?.message ?? L("Could not save feedback.", "Feedback konnte nicht gespeichert werden."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box sx={assessmentPageSx}>
      <Box sx={assessmentOuterSx}>
        <AssessmentHeader
          icon={<RateReviewOutlinedIcon />}
          title={L("KONTROL Score Feedback", "KONTROL Score Feedback")}
          subtitle={L(
            "A short anonymous-style feedback check after your score and action plan.",
            "Ein kurzer Feedback-Check nach deinem Score und Aktionsplan."
          )}
        />

        <Box sx={assessmentLayoutSx}>
          <AssessmentSidebar
            title={L("Feedback", "Feedback")}
            sections={progressWithActive}
            completed={completedRequired}
            total={requiredQuestions.length}
            overallLabel={(done, total) =>
              L(`${done} of ${total} answered`, `${done} von ${total} beantwortet`)
            }
            remainingLabel={(remaining) => {
              if (remaining > 0) return L(`${remaining} left`, `${remaining} offen`);
              return saved ? L("Submitted", "Eingereicht") : L("Ready to submit", "Bereit zum Absenden");
            }}
            sectionsHeading={L("Sections", "Abschnitte")}
            countLabel={(done, total) => `${done}/${total}`}
            skippedLabel={() => ""}
            emptyLabel={L("No feedback questions.", "Keine Feedback-Fragen.")}
          />

          <Paper elevation={0} sx={assessmentPanelSx}>
            <Box sx={assessmentContentSx}>
              <Stack spacing={2}>
                <AssessmentSectionIntro
                  eyebrow={L("Confidential", "Vertraulich")}
                  title={L("Help us improve!", "Hilf uns zu verbessern!")}
                  subtitle={L(
                    "There are no right or wrong answers. Your honest experience helps us make the score clearer, shorter and easier to trust.",
                    "Es gibt keine richtigen oder falschen Antworten. Deine ehrliche Erfahrung hilft uns, den Score klarer, kürzer und nachvollziehbarer zu machen."
                  )}
                />

                {error ? (
                  <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                    {error}
                  </Alert>
                ) : null}

                {saved ? (
                  <Alert
                    icon={<TaskAltIcon />}
                    severity="success"
                    sx={{ borderRadius: 2.5, fontWeight: 800 }}
                  >
                    {L("Feedback saved. Thank you.", "Feedback gespeichert. Danke.")}
                  </Alert>
                ) : null}

                {loading ? (
                  <Paper
                    elevation={0}
                    sx={{ ...questionCardSx, p: 2.5, display: "grid", placeItems: "center" }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <CircularProgress size={20} />
                      <Typography sx={{ fontWeight: 900, color: brand.textSecondary }}>
                        {L("Loading feedback…", "Feedback wird geladen…")}
                      </Typography>
                    </Stack>
                  </Paper>
                ) : questions.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                    {L("No feedback questions found.", "Keine Feedback-Fragen gefunden.")}
                  </Alert>
                ) : (
                  displayGroups.map((group) => (
                    <Stack key={group.key} spacing={1.25}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: brand.textPrimary,
                          fontSize: 18,
                          mt: group.startIndex === 0 ? 0 : 1,
                        }}
                      >
                        {sections[group.sectionKey].title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: brand.textMuted, mt: -0.75 }}>
                        {sections[group.sectionKey].subtitle}
                      </Typography>

                      {group.questions
                        .map((question, index) => {
                          const value = answers[question.question_code];
                          const shape = scaleShape(question);
                          const questionNumber = group.startIndex + index + 1;

                          return (
                            <Paper elevation={0} sx={questionCardSx} key={question.id}>
                              <Box sx={questionHeaderSx}>
                                <Typography sx={questionLabelSx}>
                                  {L("Question", "Frage")} {questionNumber}
                                  {!question.is_required ? ` ${L("(optional)", "(optional)")}` : ""}
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.6,
                                    fontWeight: 900,
                                    color: brand.textPrimary,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {labelForQuestion(question, lang)}
                                </Typography>
                              </Box>

                              <Box sx={questionBodySx}>
                                {shape ? (
                                  <ScaleAnswer
                                    shape={shape}
                                    value={typeof value === "string" ? value : ""}
                                    lang={lang}
                                    onChange={(next) => setSingleAnswer(question.question_code, next)}
                                  />
                                ) : question.input_type === "text" ? (
                                  <TextField
                                    fullWidth
                                    multiline
                                    minRows={4}
                                    value={String(value ?? "")}
                                    onChange={(event) =>
                                      setSingleAnswer(question.question_code, event.target.value)
                                    }
                                    placeholder={L(
                                      "Add a short note…",
                                      "Kurze Notiz hinzufügen…"
                                    )}
                                  />
                                ) : question.input_type === "multi" ? (
                                  <Box sx={answerGridSx}>
                                    {question.answer_options?.map((option) => {
                                      const selected =
                                        Array.isArray(value) && value.includes(option.value);

                                      return (
                                        <FormControlLabel
                                          key={option.value}
                                          control={<Checkbox checked={selected} />}
                                          label={labelForOption(option, lang)}
                                          onChange={() =>
                                            toggleMultiAnswer(question.question_code, option.value)
                                          }
                                          sx={getAnswerOptionSx(selected)}
                                        />
                                      );
                                    })}
                                  </Box>
                                ) : (
                                  <RadioGroup
                                    value={typeof value === "string" ? value : ""}
                                    onChange={(event) =>
                                      setSingleAnswer(question.question_code, event.target.value)
                                    }
                                  >
                                    <Box sx={answerGridSx}>
                                      {question.answer_options?.map((option) => {
                                        const selected = value === option.value;

                                        return (
                                          <FormControlLabel
                                            key={option.value}
                                            value={option.value}
                                            control={<Radio />}
                                            label={labelForOption(option, lang)}
                                            sx={getAnswerOptionSx(selected)}
                                          />
                                        );
                                      })}
                                    </Box>
                                  </RadioGroup>
                                )}
                              </Box>
                            </Paper>
                          );
                        })}
                    </Stack>
                  ))
                )}

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  justifyContent="space-between"
                >
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => handlePageChange("KontrolScore")}
                    sx={assessmentOutlineButtonSx}
                  >
                    {L("Back to score", "Zurück zum Score")}
                  </Button>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={!canSubmit}
                    onClick={submit}
                    sx={assessmentPrimaryButtonSx}
                  >
                    {saving
                      ? L("Saving…", "Speichert…")
                      : L("Submit feedback", "Feedback absenden")}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
